import { useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleDollarSign, Cog, Gauge, Truck, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@marine/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@marine/ui/card";
import { Input } from "@marine/ui/input";
import { Label } from "@marine/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@marine/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@marine/ui/select";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@marine/ui/sheet";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useDealershipSettings } from "@/modules/crm/hooks/useDealershipSettings";
import { useCreateEngine, useEngine } from "@/modules/inventory/hooks/useEngines";
import { useCreateTrailer, useTrailer } from "@/modules/inventory/hooks/useTrailers";
import { useLocations } from "@/modules/inventory/hooks/useLocations";
import { useStatuses } from "@/modules/inventory/hooks/useStatuses";
import { engineApi } from "@/modules/inventory/services/engineApi";
import { trailerApi } from "@/modules/inventory/services/trailerApi";
import type { AssetDocument, AssetImage, Engine } from "@/modules/inventory/types/engine.types";
import type { Trailer } from "@/modules/inventory/types/trailer.types";
import type { Location, ProvenanceEntry } from "@/modules/inventory/types/boat.types";
import { CopilotSidebar } from "../canvas/CopilotSidebar";
import { MediaCard } from "../canvas/MediaCard";
import { ProvenanceChip } from "../canvas/ProvenanceChip";
import { useFactoryPacketExtract } from "../canvas/hooks/useFactoryPacketExtract";
import type { TempMediaItem } from '../canvas/types';
import type { BoatDocument } from "../documents/hooks/useDocuments";
import { useCombinedUpload } from "../documents/hooks/useCombinedUpload";
import {
  NONE,
  buildPayload,
  detailFieldsFor,
  firstValidationError,
  initialValues,
  pricingFieldsFor,
  sectionsFor,
  type AssetType,
} from "../asset-form/asset-form-config";
import { useFieldOptions } from "@/modules/core/hooks/useFieldOptions";
import { useCan } from "@/modules/tenant/hooks/useTenantAuth";
import { useAssetDraftSync } from "./useAssetDraftSync";
import { useAssetExtractFill } from "./useAssetExtractFill";
import {
  assetTintClass,
  isLowConfidenceAssetEntry,
  type AssetProvenanceMap,
} from "./assetProvenance";

const INGEST_ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";

/** Debounced auto-save delay once a server draft exists. */
const AUTOSAVE_MS = 900;

/** Map a committed asset image into the committed MediaCard shape (boat parity). */
function mapAssetImageToCard(img: AssetImage): TempMediaItem {
  return {
    clientId: `existing-${img.id}`,
    tempMediaId: "",
    mediaId: img.id,
    url: img.url,
    fileName: img.file_name,
    size: img.size ?? 0,
    uploading: false,
    progress: 100,
  };
}

/** Map a committed asset document into the committed-document MediaCard shape. */
function mapAssetDocumentToCard(doc: AssetDocument): BoatDocument {
  return { ...doc, doc_type: (doc.doc_type ?? null) as BoatDocument["doc_type"], created_at: null };
}

/**
 * The boat canvas's SectionCard pattern (CanvasFormSections, CANVAS-14):
 * accent header bar with icon + 14px CardTitle, 16px-spaced body. The asset
 * canvas has no scroll-spy nav, so no section ids are required.
 */
function AssetSectionCard({
  title,
  Icon,
  children,
}: {
  title: string;
  Icon: ComponentType<{ className?: string }>;
  children: ReactNode;
}) {
  return (
    <section>
      <Card>
        <CardHeader className="min-h-[34px] bg-accent/50">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            <CardTitle className="text-2sm">{title}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">{children}</CardContent>
      </Card>
    </section>
  );
}

interface AssetCanvasProps {
  type: AssetType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the created asset's id. */
  onCreated?: (id: string) => void;
  /** Resume an existing draft asset (?resume=<id>) — hydrates fields + media. */
  resumeAssetId?: string;
}

/**
 * SP3-full — the Add Engine/Trailer canvas at boat-canvas parity: the first
 * dropped file silently creates a Draft asset (autogen EN-/TR- stock #),
 * commits the file via the item-generic combined-upload (so it PERSISTS as
 * draft media), then triggers the async factory-packet extraction — per-field
 * events stream over the per-item copilot channel into the form LIVE
 * (useAssetExtractFill) while the merge writes the entity onto the draft's
 * own detail row server-side; the CopilotSidebar narrates in the right
 * column and the 4-state provenance tints/chips render from the field-sources
 * projection. Field edits auto-save to the draft (debounced PATCH re-asserting
 * Draft); Create publishes the final payload out of Draft; Close keeps the
 * draft, resumable via `resumeAssetId`.
 */
export function AssetCanvas({ type, open, onOpenChange, onCreated, resumeAssetId }: AssetCanvasProps) {
  // ACL-09 (#1408, D-08): cost, invoice and freight are rendered, seeded and
  // posted only for finance.cost.view holders, so a stale cached asset or an
  // AI fill never sends them for anyone else.
  const canViewCost = useCan('finance.cost.view');
  const [values, setValues] = useState(() => initialValues(type, null, canViewCost));
  // SP3-full slice 3 (D5) — per-field provenance map (the boat ProvenanceEntry
  // shape): stream events + field-sources hydration write entries; the
  // canvas-field tint classes + ProvenanceChip render from it.
  const [prov, setProv] = useState<AssetProvenanceMap>({});
  const [isDragging, setIsDragging] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [committedImages, setCommittedImages] = useState<AssetImage[]>([]);
  const [committedDocuments, setCommittedDocuments] = useState<AssetDocument[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // True once resume data has been written into the form — guards the
  // hydration effect from clobbering user edits on background refetches.
  const hydratedRef = useRef(false);
  // Publish-in-progress: suppress the debounced auto-save so a late PATCH
  // can't re-assert status_id=Draft after the final publish PATCH landed.
  const suspendAutoSaveRef = useRef(false);
  // Manual-edit-wins for the STREAM and the hydration paths: fields the
  // dealer typed into this session; a streamed re-read, field-sources
  // hydration, or canonical reconcile never clobbers them.
  const userEditedRef = useRef<Set<string>>(new Set());

  const isDesktop = useMediaQuery("(min-width: 1280px)");
  // Lifted copilot collapse (boat-canvas CANVAS-16 pattern): the grid's second
  // track and the sidebar shrink in lock-step; persisted per device.
  const [copilotCollapsed, setCopilotCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("asset-canvas-copilot-collapsed") === "true";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("asset-canvas-copilot-collapsed", String(copilotCollapsed));
    } catch {
      // best-effort persistence only
    }
  }, [copilotCollapsed]);

  const { data: dealershipSettings } = useDealershipSettings();
  const tenantId = dealershipSettings?.tenant_id ?? null;

  const draftSync = useAssetDraftSync(type);
  // #581 Task 9 — suppressExtract=true: this canvas drives extraction itself
  // (useAssetExtractFill via useFactoryPacketExtract below), so its commits
  // must suppress the backend's auto-trigger propose-mode extraction (#581
  // Task 5) to avoid a second, competing extraction job for the same upload
  // (mirrors LiveFillCanvas's boat-canvas instance).
  const upload = useCombinedUpload(
    draftSync.assetId,
    type === "engine" ? "/engines" : "/trailers",
    true,
  );

  // SP3-full slice 2 (D1/D3): the async streaming pipeline. The trigger hits
  // the item-generic factory-packet endpoint with the draft asset id; the
  // ai-engine streams {type:'field'} events on tenant.{t}.copilot.{assetId},
  // which useAssetExtractFill maps onto the form below.
  const factoryPacket = useFactoryPacketExtract();
  const queryClient = useQueryClient();
  const api = type === "engine" ? engineApi : trailerApi;
  const fieldSourcesKey = useMemo(
    () => [type === "engine" ? "engines" : "trailers", "field-sources", draftSync.assetId] as const,
    [type, draftSync.assetId],
  );

  useAssetExtractFill(type, tenantId, draftSync.assetId, {
    isUserEdited: (key) => userEditedRef.current.has(key),
    onField: (key, value, meta) => {
      setValues((prev) => (key in prev ? { ...prev, [key]: value } : prev));
      setProv((prev) => ({
        ...prev,
        [key]: {
          source: meta.source,
          value,
          confidence: meta.confidence,
          updatedAt: new Date().toISOString(),
        },
      }));
    },
    onDone: () => {
      factoryPacket.markReadingComplete();
      // The merge just persisted canonical values + field_sources — refetch
      // the authoritative provenance (states incl. conflicts) for the tints.
      void queryClient.invalidateQueries({ queryKey: fieldSourcesKey });
    },
    onFailed: factoryPacket.markReadingComplete,
  });

  // Detail data — the resume hydration source AND the post-merge reconcile
  // backstop (D6): enabled whenever a draft exists, so the terminal-done
  // invalidate refetches the merged canonical row.
  const detailId = (resumeAssetId ?? draftSync.assetId) && open ? (resumeAssetId ?? draftSync.assetId)! : "";
  const engineDetail = useEngine(type === "engine" ? detailId : "");
  const trailerDetail = useTrailer(type === "trailer" ? detailId : "");
  const assetDetail = (type === "engine" ? engineDetail.data : trailerDetail.data) as
    | Engine
    | Trailer
    | undefined;

  // SP3-full slice 3 (D5) — authoritative provenance from the merge's
  // field_sources projection (flat form keys), fetched on resume and after
  // each terminal done.
  const { data: fieldSources } = useQuery({
    queryKey: fieldSourcesKey,
    queryFn: () => api.getFieldSources(draftSync.assetId!),
    enabled: open && !!draftSync.assetId,
  });

  // Reset per open so a closed canvas never leaks state into the next one.
  useEffect(() => {
    if (open) {
      setValues(initialValues(type, null, canViewCost));
      setProv({});
      setCommittedImages([]);
      setCommittedDocuments([]);
      setIsPublishing(false);
      hydratedRef.current = false;
      suspendAutoSaveRef.current = false;
      userEditedRef.current = new Set();
      draftSync.resetDraft();
      upload.clear();
      factoryPacket.markReadingComplete();
      if (resumeAssetId) {
        draftSync.setAssetId(resumeAssetId);
      }
    }
    // draftSync/upload/factoryPacket are stable hook returns; keying on
    // open/type/resume id mirrors the SP3-lite reset effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, resumeAssetId]);

  // Detail hydration. First arrival on a resume = FULL hydration; after that
  // every refetch (the terminal-done / auto-save invalidates) is a RECONCILE:
  // fill only EMPTY, non-user-edited fields from the merged canonical row
  // (D6 — the dropped-WS-event backstop) and sync the committed media arrays
  // (server truth; present on the detail payload only).
  useEffect(() => {
    if (!open || !assetDetail) return;

    if (resumeAssetId && !hydratedRef.current) {
      hydratedRef.current = true;
      setValues(initialValues(type, assetDetail, canViewCost));
    } else {
      const canonical = initialValues(type, assetDetail, canViewCost);
      setValues((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const [key, value] of Object.entries(canonical)) {
          const empty = next[key] === "" || next[key] === NONE;
          const meaningful = value !== "" && value !== NONE;
          if (empty && meaningful && !userEditedRef.current.has(key)) {
            next[key] = value;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }

    if (assetDetail.images !== undefined) setCommittedImages(assetDetail.images);
    if (assetDetail.documents !== undefined) setCommittedDocuments(assetDetail.documents);
  }, [open, resumeAssetId, assetDetail, type, canViewCost]);

  // Provenance hydration (D5): the field-sources projection is authoritative
  // (merge states incl. conflicts). Locally user-edited keys keep their
  // manual entry — the dealer's in-session edit outranks a stale projection.
  useEffect(() => {
    if (!open || !fieldSources) return;
    setProv((prev) => {
      const next: AssetProvenanceMap = { ...prev };
      for (const [key, entry] of Object.entries(fieldSources)) {
        if (userEditedRef.current.has(key)) continue;
        next[key] = entry as AssetProvenanceMap[string];
      }
      return next;
    });
  }, [open, fieldSources]);

  const { data: statusesData } = useStatuses();
  const { data: locationsResponse } = useLocations();
  const statuses = statusesData?.items ?? [];
  const locations = useMemo(
    () => (Array.isArray(locationsResponse) ? (locationsResponse as Location[]) : []),
    [locationsResponse],
  );

  const createEngine = useCreateEngine();
  const createTrailer = useCreateTrailer();
  const createMutation = type === "engine" ? createEngine : createTrailer;

  // Catalog-fed select options (Slice 3) — must be called unconditionally at
  // component top level (React hooks rule). Empty until the vocabulary loads.
  const driveOptions = useFieldOptions("drive_type");
  const fuelOptions = useFieldOptions("fuel_type");
  const axleOptions = useFieldOptions("trailer_axles");

  const isReading = factoryPacket.isReading;

  /**
   * Drop pipeline (boat-canvas parity, slice 2 D3): stage the files →
   * silently get-or-create the Draft asset → commit the batch against it
   * (the files are now real draft media) → trigger the ASYNC extraction over
   * the committed media. Fields then stream into the form live; each
   * streamed write triggers the debounced auto-save, so the AI fill persists.
   */
  const runIngest = async (incoming: FileList | File[]) => {
    const accepted = INGEST_ACCEPT.split(",");
    const files = Array.from(incoming).filter((f) => accepted.includes(f.type));
    if (files.length === 0) {
      toast.error("Drop a PDF or an image (JPG, PNG, WebP).");
      return;
    }

    const staged = upload.onDrop(files);

    const id = await draftSync.ensureDraft();
    if (!id) {
      toast.error(draftSync.saveError ?? "Could not create a draft to attach the file to.");
      upload.clear();
      return;
    }

    try {
      const result = await upload.commit({ boatId: id, items: staged });
      setCommittedImages(result.images as AssetImage[]);
      setCommittedDocuments((result.documents ?? []) as AssetDocument[]);
      // Commit landed — extract over the just-committed media (the endpoint's
      // additive filter skips anything already extracted).
      factoryPacket.trigger(id);
    } catch {
      toast.error("Could not save the file to the draft — retry the drop.");
    }
  };

  // Debounced auto-save once a server draft exists. Streamed AI fills persist
  // too (the stream's setValues triggers this effect like any edit).
  useEffect(() => {
    if (!open || !draftSync.assetId || suspendAutoSaveRef.current) return;
    const timer = setTimeout(() => {
      if (suspendAutoSaveRef.current) return;
      void draftSync.saveDraft(buildPayload(type, values, canViewCost));
    }, AUTOSAVE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, draftSync.assetId, open, type, canViewCost]);

  // Manual edit wins: typing into a field registers it (a streamed re-read
  // never clobbers it) and flips its provenance to the neutral "Edited by
  // you" state — the boat canvas's 4th tint state.
  const set = (key: string) => (value: string) => {
    userEditedRef.current.add(key);
    setValues((prev) => ({ ...prev, [key]: value }));
    setProv((prev) => ({
      ...prev,
      [key]: {
        source: "manual",
        value,
        confidence: 1,
        userEdited: true,
        updatedAt: new Date().toISOString(),
      },
    }));
  };

  const onCreate = () => {
    const payload = buildPayload(type, values, canViewCost);

    // Draft path: the row already exists — publish is the final PATCH (with
    // the user's chosen status), the only write that leaves Draft.
    if (draftSync.assetId) {
      setIsPublishing(true);
      suspendAutoSaveRef.current = true;
      const id = draftSync.assetId;
      draftSync
        .publishDraft(payload)
        .then(() => {
          onOpenChange(false);
          onCreated?.(id);
        })
        .catch((error) => {
          suspendAutoSaveRef.current = false;
          toast.error(firstValidationError(error, `Could not create the ${type}.`));
        })
        .finally(() => setIsPublishing(false));
      return;
    }

    createMutation.mutate(payload, {
      onSuccess: (saved) => {
        onOpenChange(false);
        onCreated?.((saved as Engine | Trailer).id);
      },
      onError: (error) =>
        toast.error(firstValidationError(error, `Could not create the ${type}.`)),
    });
  };

  const TypeIcon = type === "engine" ? Cog : Truck;
  const sections = sectionsFor(type);
  const fieldDefs = useMemo(
    () => new Map(detailFieldsFor(type).map((field) => [field.key, field])),
    [type],
  );
  const isCreatePending = createMutation.isPending || isPublishing;
  const hasMedia =
    upload.items.length > 0 || committedImages.length > 0 || committedDocuments.length > 0;

  const savedIndicator = draftSync.assetId
    ? draftSync.isSaving || draftSync.isCreating || upload.committing
      ? "Saving draft…"
      : draftSync.saveError
        ? "Draft save failed — edits retry automatically"
        : draftSync.lastSavedAt
          ? "Draft saved"
          : null
    : null;

  // SP3-full slice 3 (D5) — the boat canvas's label row: label + provenance
  // chip; the `canvas-field` wrapper class drives the 4-state tint via the
  // shared canvas.css rules (violet / amber / conflict / neutral-edited).
  const fieldLabel = (key: string, label: string) => (
    <div className="flex min-h-5 items-center justify-between gap-1">
      <Label className="text-xs">{label}</Label>
      <ProvenanceChip
        fieldName={label}
        entry={(prov[key] ?? null) as ProvenanceEntry | ProvenanceEntry[] | null}
        isLowConfidence={isLowConfidenceAssetEntry(prov[key])}
      />
    </div>
  );

  // Field controls are DEFAULT size (14px body inputs) — boat-canvas FieldRow
  // parity; the compact sm variants belong to the SP2-D edit dialog only.
  const selectField = (
    key: string,
    label: string,
    options: { value: string; label: string }[],
  ) => (
    <div className={cn("space-y-2 canvas-field", assetTintClass(prov[key]))}>
      {fieldLabel(key, label)}
      <Select value={values[key]} onValueChange={set(key)}>
        <SelectTrigger>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>—</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const textField = (key: string, label: string, kind: "text" | "number", placeholder?: string) => (
    <div className={cn("space-y-2 canvas-field", assetTintClass(prov[key]))}>
      {fieldLabel(key, label)}
      <Input
        type={kind === "number" ? "number" : "text"}
        value={values[key]}
        onChange={(e) => set(key)(e.target.value)}
        placeholder={placeholder ?? label}
      />
    </div>
  );

  const selectOptionsFor = (key: string, fallback: { value: string; label: string }[]) => {
    if (key === "drive_type") return driveOptions.options;
    if (key === "fuel_type") return fuelOptions.options;
    if (key === "trailer_axles") return axleOptions.options;
    return fallback;
  };

  const renderDetailField = (key: string) => {
    const field = fieldDefs.get(key);
    if (!field) return null;
    return (
      <div key={key} className="contents">
        {field.kind === "select"
          ? selectField(field.key, field.label, selectOptionsFor(field.key, [...(field.options ?? [])]))
          : textField(field.key, field.label, field.kind)}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        // Mirrors LiveFillCanvas's side-sheet overrides (inset rounded sheet,
        // right-aligned), capped narrower than the boat canvas — assets are
        // one section, not nine — but wide enough for the copilot column.
        className="gap-0 w-[calc(100vw-2.5rem)] max-w-[1280px] sm:max-w-[1280px] inset-5 border start-auto h-auto rounded-lg p-0 [&_[data-slot=sheet-close]]:top-3.5 [&_[data-slot=sheet-close]]:end-5"
        side="right"
      >
        <div className="flex flex-col h-[calc(100dvh-2.5rem)]">
        {/* Header — boat-canvas parity: compact, title only (no tag line). */}
        <SheetHeader className="border-b py-3 px-5 border-border">
          <SheetTitle className="text-sm font-medium">
            Add New {type === "engine" ? "Engine" : "Trailer"}
          </SheetTitle>
        </SheetHeader>

        <div
          className={cn(
            "grid flex-1 min-h-0",
            // Boat-canvas CANVAS-16: the copilot track follows the lifted
            // collapse boolean on desktop (340px ↔ 48px rail), animated as one
            // motion. Below 1280px CopilotSidebar renders its own fixed FAB +
            // bottom sheet, so the grid is a single column.
            isDesktop
              ? cn(
                  "transition-[grid-template-columns] duration-300 ease-in-out motion-reduce:transition-none",
                  copilotCollapsed ? "grid-cols-[1fr_48px]" : "grid-cols-[1fr_340px]",
                )
              : "grid-cols-1",
          )}
        >
        <main className="space-y-5 overflow-y-auto px-5 py-4">
          {/* Dropzone — cloned from the boat canvas FactoryPacketDropzone. */}
          <input
            ref={fileInputRef}
            type="file"
            accept={INGEST_ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) void runIngest(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              void runIngest(e.dataTransfer.files);
            }}
            aria-label={`Upload ${type} document`}
            className={cn(
              "group flex w-full items-center text-center",
              "rounded-lg border-2 border-dashed transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isDragging
                ? "border-primary bg-primary/10"
                : "border-border/70 bg-muted/20 hover:border-primary/70 hover:bg-accent/60",
              "min-h-[140px] flex-col justify-center gap-3 p-6",
            )}
          >
            <UploadCloud
              className={cn(
                "size-10 shrink-0 transition-colors",
                isDragging ? "text-primary" : "text-muted-foreground group-hover:text-foreground",
              )}
              role="img"
            />
            <div className="space-y-1">
              <p className="text-sm font-normal text-foreground">
                {isDragging
                  ? "Drop your document to read it"
                  : isReading
                    ? "now reading your document…"
                    : `Drop ${type === "engine" ? "an engine" : "a trailer"} spec sheet or COO`}
              </p>
              <p className="text-sm font-normal text-muted-foreground">
                PDF or image — saved to the draft, or browse
              </p>
            </div>
          </button>

          {/* Hero media grid — committed (draft media from commit/resume) +
              staged cards, the boat canvas's MediaCard idiom. boatId is null
              on purpose: the committed-image boat mutations (⭐/delete) have
              no asset endpoints yet — guarded no-ops this slice. */}
          {hasMedia && (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {committedImages.map((img) => (
                <MediaCard
                  key={`ci-${img.id}`}
                  entry={{ kind: "committed", item: mapAssetImageToCard(img) }}
                  boatId={null}
                />
              ))}
              {committedDocuments.map((doc) => (
                <MediaCard
                  key={`cd-${doc.id}`}
                  entry={{ kind: "committed-document", item: mapAssetDocumentToCard(doc) }}
                  boatId={null}
                />
              ))}
              {upload.items.map((item) => (
                <MediaCard
                  key={item.clientId}
                  entry={{ kind: "staged", item }}
                  boatId={null}
                  onReroute={upload.reroute}
                  onRemoveStaged={upload.remove}
                  committing={upload.committing}
                />
              ))}
            </div>
          )}

          {/* Form sections — the boat canvas's SectionCard structure
              (user-approved 3-section layout): Identity (with the boat-style
              New/Used toggle) / Specifications / Inventory & Pricing. */}
          {sections.map((section) => (
            <AssetSectionCard
              key={section.id}
              title={section.title}
              Icon={section.id === "identity" ? TypeIcon : Gauge}
            >
              {section.id === "identity" && (
                <div className={cn("space-y-2 canvas-field", assetTintClass(prov.condition))}>
                  {fieldLabel("condition", "Condition")}
                  <ToggleGroup
                    type="single"
                    variant="outline"
                    value={values.condition === NONE ? undefined : values.condition}
                    onValueChange={(value) => {
                      // Guard against deselection: Radix fires '' on click-active
                      // (the boat ConditionRow pattern).
                      if (value) set("condition")(value);
                    }}
                    className="w-full"
                  >
                    <ToggleGroupItem value="new" className="flex-1 whitespace-nowrap">
                      New
                    </ToggleGroupItem>
                    <ToggleGroupItem value="used" className="flex-1 whitespace-nowrap">
                      Used
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              )}
              <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                {section.keys.map(renderDetailField)}
              </div>
            </AssetSectionCard>
          ))}

          <AssetSectionCard title="Inventory & Pricing" Icon={CircleDollarSign}>
            <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              {textField("inventory_number", "Stock #", "text", "Auto-generated if blank")}
              {textField("our_price", "Price", "number")}
              {/* SP4 slice 1 (D3) — the rest of the 7-field pricing card. */}
              {pricingFieldsFor(canViewCost).map((field) => textField(field.key, field.label, "number"))}
              {selectField("status_id", "Status", statuses.map((s) => ({ value: s.id, label: s.label })))}
              {selectField("location_id", "Location", locations.map((l) => ({ value: l.id, label: l.name })))}
            </div>
          </AssetSectionCard>
        </main>

        {/* Right: copilot column (slice 2) — the boat canvas's CopilotSidebar
            scoped to the draft asset id. Null until the first drop creates
            the draft; the sidebar renders its empty session state until then.
            One section only, so the jump-to-field hook is a no-op. */}
        <CopilotSidebar
          tenantId={tenantId}
          inventoryItemId={draftSync.assetId}
          onJumpToField={() => {}}
          collapsed={copilotCollapsed}
          onToggleCollapse={() => setCopilotCollapsed((v) => !v)}
        />
        </div>

        <SheetFooter className="flex-row items-center gap-2 border-t px-5 py-3">
          {savedIndicator && (
            <span className="me-auto text-xs text-muted-foreground" data-testid="draft-saved-indicator">
              {savedIndicator}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            className={savedIndicator ? undefined : "ms-auto"}
            onClick={() => onOpenChange(false)}
            disabled={isCreatePending}
          >
            Close
          </Button>
          <Button size="sm" onClick={onCreate} disabled={isCreatePending || isReading}>
            {isCreatePending ? "Creating..." : `Create ${type}`}
          </Button>
        </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
}
